import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ApproveMemberResponse,
  BlockMemberApplicationErrors,
  BlockMemberResponse,
  BulkApproveMembersResponse,
  BulkBlockMembersResponse,
  BulkDeleteMembersByFilterOptions,
  BulkDeleteMembersByFilterResponse,
  BulkDeleteMembersResponse,
  CreateMemberOptions,
  DeleteMemberAddressesResponse,
  DeleteMemberEmailsResponse,
  DeleteMemberPhonesResponse,
  DeleteMemberValidationErrors,
  DeleteMyMemberOptions,
  DeleteMyMemberValidationErrors,
  DisconnectMemberResponse,
  GetCurrentMemberApplicationErrors,
  GetCurrentMemberOptions,
  GetMemberApplicationErrors,
  GetMemberOptions,
  GetMyMemberResponse,
  JoinCommunityApplicationErrors,
  JoinCommunityResponse,
  LeaveCommunityResponse,
  ListMembersOptions,
  ListMembersResponse,
  Member,
  MemberCreatedEnvelope,
  MemberDeletedEnvelope,
  MemberQuery,
  MemberUpdatedEnvelope,
  MembersQueryBuilder,
  MuteMemberResponse,
  QueryMembersOptions,
  QueryMembersResponse,
  UnmuteMemberResponse,
  UpdateCurrentMemberSlugApplicationErrors,
  UpdateMember,
  UpdateMemberSlugApplicationErrors,
  UpdateMemberSlugResponse,
  UpdateMemberValidationErrors,
  UpdateMySlugResponse,
  approveMember as universalApproveMember,
  blockMember as universalBlockMember,
  bulkApproveMembers as universalBulkApproveMembers,
  bulkBlockMembers as universalBulkBlockMembers,
  bulkDeleteMembers as universalBulkDeleteMembers,
  bulkDeleteMembersByFilter as universalBulkDeleteMembersByFilter,
  createMember as universalCreateMember,
  deleteMember as universalDeleteMember,
  deleteMemberAddresses as universalDeleteMemberAddresses,
  deleteMemberEmails as universalDeleteMemberEmails,
  deleteMemberPhones as universalDeleteMemberPhones,
  deleteMyMember as universalDeleteMyMember,
  disconnectMember as universalDisconnectMember,
  getCurrentMember as universalGetCurrentMember,
  getMember as universalGetMember,
  joinCommunity as universalJoinCommunity,
  leaveCommunity as universalLeaveCommunity,
  listMembers as universalListMembers,
  muteMember as universalMuteMember,
  queryMembers as universalQueryMembers,
  typedQueryMembers as universalTypedQueryMembers,
  unmuteMember as universalUnmuteMember,
  updateCurrentMemberSlug as universalUpdateCurrentMemberSlug,
  updateMember as universalUpdateMember,
  updateMemberSlug as universalUpdateMemberSlug,
} from './members-v1-member-members.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/members' };

export function updateCurrentMemberSlug(
  httpClient: HttpClient
): UpdateCurrentMemberSlugSignature {
  return (slug: string) =>
    universalUpdateCurrentMemberSlug(
      slug,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateCurrentMemberSlugSignature {
  /**
   * Updates the currently logged in member's slug.
   *
   * The `slug` is the end of a member's URL that refers to a specific logged-in member. For example, if a member's URL is `https://example.com/member/{my-member-slug}`, the slug is `my-member-slug`. The slug is case-sensitive and is generally derived from the member's `nickname`; otherwise, it's derived from the `loginEmail`.
   *
   * > **Note:**
   * > Only logged-in members can call this method without elevated permissions.
   * > To call this method as a different identity, [elevated permissions](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/about-elevated-permissions) are required.
   * @param - New slug.
   */
  (slug: string): Promise<
    NonNullablePaths<
      UpdateMySlugResponse,
      | `member.status`
      | `member.contact.phones`
      | `member.contact.emails`
      | `member.contact.addresses`
      | `member.contact.addresses.${number}.streetAddress.number`
      | `member.contact.addresses.${number}.streetAddress.name`
      | `member.profile.photo._id`
      | `member.profile.photo.url`
      | `member.profile.photo.height`
      | `member.profile.photo.width`
      | `member.privacyStatus`
      | `member.activityStatus`,
      7
    > & {
      __applicationErrorsType?: UpdateCurrentMemberSlugApplicationErrors;
    }
  >;
}

export function updateMemberSlug(
  httpClient: HttpClient
): UpdateMemberSlugSignature {
  return (_id: string, slug: string) =>
    universalUpdateMemberSlug(
      _id,
      slug,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateMemberSlugSignature {
  /**
   * Updates a member's slug.
   *
   * To update the current member's slug, call [Update My Slug](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/update-my-slug).
   * @param - Member ID.
   * @param - New slug.
   */
  (_id: string, slug: string): Promise<
    NonNullablePaths<
      UpdateMemberSlugResponse,
      | `member.status`
      | `member.contact.phones`
      | `member.contact.emails`
      | `member.contact.addresses`
      | `member.contact.addresses.${number}.streetAddress.number`
      | `member.contact.addresses.${number}.streetAddress.name`
      | `member.profile.photo._id`
      | `member.profile.photo.url`
      | `member.profile.photo.height`
      | `member.profile.photo.width`
      | `member.privacyStatus`
      | `member.activityStatus`,
      7
    > & {
      __applicationErrorsType?: UpdateMemberSlugApplicationErrors;
    }
  >;
}

export function joinCommunity(httpClient: HttpClient): JoinCommunitySignature {
  return () =>
    universalJoinCommunity(
      // @ts-ignore
      { httpClient }
    );
}

interface JoinCommunitySignature {
  /**
   * Joins the current member to the site community and sets their profile to public.
   *
   * When a member's profile is public, they have access to the site's
   * [Members Area](https://support.wix.com/en/article/site-members-about-the-members-area)
   * features, such as chat, forum, and followers,
   * and their profile is visible to other members and site visitors.
   *
   * > **Note:**
   * > Only logged-in members can call this method without elevated permissions.
   * > To call this method as a different identity, [elevated permissions](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/about-elevated-permissions) are required.
   * @returns Member profile.
   */
  (): Promise<
    NonNullablePaths<
      JoinCommunityResponse,
      | `member.status`
      | `member.contact.phones`
      | `member.contact.emails`
      | `member.contact.addresses`
      | `member.contact.addresses.${number}.streetAddress.number`
      | `member.contact.addresses.${number}.streetAddress.name`
      | `member.profile.photo._id`
      | `member.profile.photo.url`
      | `member.profile.photo.height`
      | `member.profile.photo.width`
      | `member.privacyStatus`
      | `member.activityStatus`,
      7
    > & {
      __applicationErrorsType?: JoinCommunityApplicationErrors;
    }
  >;
}

export function leaveCommunity(
  httpClient: HttpClient
): LeaveCommunitySignature {
  return () =>
    universalLeaveCommunity(
      // @ts-ignore
      { httpClient }
    );
}

interface LeaveCommunitySignature {
  /**
   * Removes the current member from the site community and sets their profile to private.
   *
   * When a member's profile is private,
   * they do not have access to the site's
   * [Members Area](https://support.wix.com/en/article/site-members-about-the-members-area)
   * features, such as chat, forum, and followers,
   * and their profile is hidden from other members and site visitors.
   *
   * > **Notes:**
   * > + If a member leaves the site's community, their content, such as forum posts and blog comments, remain publicly visible.
   *
   * > + Only logged-in members can call this method without elevated permissions.
   * > + To call this method as a different identity, [elevated permissions](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/about-elevated-permissions) are required.
   * @returns Member profile.
   */
  (): Promise<
    NonNullablePaths<
      LeaveCommunityResponse,
      | `member.status`
      | `member.contact.phones`
      | `member.contact.emails`
      | `member.contact.addresses`
      | `member.contact.addresses.${number}.streetAddress.number`
      | `member.contact.addresses.${number}.streetAddress.name`
      | `member.profile.photo._id`
      | `member.profile.photo.url`
      | `member.profile.photo.height`
      | `member.profile.photo.width`
      | `member.privacyStatus`
      | `member.activityStatus`,
      7
    >
  >;
}

export function getCurrentMember(
  httpClient: HttpClient
): GetCurrentMemberSignature {
  return (options?: GetCurrentMemberOptions) =>
    universalGetCurrentMember(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetCurrentMemberSignature {
  /**
   * Retrieves the current member.
   *
   * >**Note:**
   * >This method requires [visitor or member authentication](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).
   *
   * To retrieve a member by `ID`, call [Get Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/get-member).
   * @returns Member profile.
   */
  (options?: GetCurrentMemberOptions): Promise<
    NonNullablePaths<
      GetMyMemberResponse,
      | `member.status`
      | `member.contact.phones`
      | `member.contact.emails`
      | `member.contact.addresses`
      | `member.contact.addresses.${number}.streetAddress.number`
      | `member.contact.addresses.${number}.streetAddress.name`
      | `member.profile.photo._id`
      | `member.profile.photo.url`
      | `member.profile.photo.height`
      | `member.profile.photo.width`
      | `member.privacyStatus`
      | `member.activityStatus`,
      7
    > & {
      __applicationErrorsType?: GetCurrentMemberApplicationErrors;
    }
  >;
}

export function getMember(httpClient: HttpClient): GetMemberSignature {
  return (_id: string, options?: GetMemberOptions) =>
    universalGetMember(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetMemberSignature {
  /**
   * Retrieves a member by ID.
   *
   * >**Note:** The returned Member object contains only the fields that were explicitly added to the Member object. Custom Contact fields are **not** automatically added to the Member object. They must be [added to the Member object by the site owner](https://support.wix.com/en/article/site-members-customizing-your-member-profile-fields).
   *
   * @param - Member ID.
   * @param - Fieldset options.
   * @returns The requested member.
   */
  (_id: string, options?: GetMemberOptions): Promise<
    NonNullablePaths<
      Member,
      | `status`
      | `contact.phones`
      | `contact.emails`
      | `contact.addresses`
      | `contact.addresses.${number}.streetAddress.number`
      | `contact.addresses.${number}.streetAddress.name`
      | `profile.photo._id`
      | `profile.photo.url`
      | `profile.photo.height`
      | `profile.photo.width`
      | `privacyStatus`
      | `activityStatus`,
      6
    > & {
      __applicationErrorsType?: GetMemberApplicationErrors;
    }
  >;
}

export function listMembers(httpClient: HttpClient): ListMembersSignature {
  return (options?: ListMembersOptions) =>
    universalListMembers(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListMembersSignature {
  /**
   * Lists site members, given the provided paging and fieldsets.
   *
   * A member's default privacy status is `"PRIVATE"`. Private members aren't returned in the response if a [site visitor](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities#site-visitor) or [site member](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities#site-member) identity calls this method. Learn more about [managing your member privacy settings](https://support.wix.com/en/article/site-members-managing-your-member-privacy-settings).
   * @param - Options for paging, sorting, and specifying fields to return.
   */
  (options?: ListMembersOptions): Promise<
    NonNullablePaths<
      ListMembersResponse,
      | `members`
      | `members.${number}.status`
      | `members.${number}.profile.photo._id`
      | `members.${number}.profile.photo.url`
      | `members.${number}.profile.photo.height`
      | `members.${number}.profile.photo.width`
      | `members.${number}.privacyStatus`
      | `members.${number}.activityStatus`,
      6
    >
  >;
}

export function queryMembers(httpClient: HttpClient): QueryMembersSignature {
  return (options?: QueryMembersOptions) =>
    universalQueryMembers(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryMembersSignature {
  /**
   * Retrieves a list of up to 100 members, given the specified filters, fieldsets, sorting and paging, and returns a `MembersQueryBuilder` object.
   *
   * The returned object contains the query definition which is typically used to run the query using the `find()` method.
   *
   * You can refine the query by chaining `MembersQueryBuilder` methods onto the query. `MembersQueryBuilder` methods enable you to sort, filter, and control the results that `queryMembers()` returns. The methods that are chained to `queryMembers()` are applied in the order they are called.
   *
   * `queryMembers()` runs with the following `MembersQueryBuilder` defaults that you can override:
   * - `skip`: `0`
   * - `limit`: `100`
   *
   * Currently supported fields for sorting:
   * - `profile.nickname`
   * - `contact.firstName`
   * - `contact.lastName`
   * - `createdDate`
   * - `lastLoginDate`
   *
   * The following `MembersQueryBuilder` methods are supported for the `queryMembers()` method. For a full description of the Locations object, see the returned for the `items` property in `MembersQueryResult`.
   * @param - Query options.
   */
  (options?: QueryMembersOptions): MembersQueryBuilder;
}

export function typedQueryMembers(
  httpClient: HttpClient
): TypedQueryMembersSignature {
  return (query: MemberQuery, options?: QueryMembersOptions) =>
    universalTypedQueryMembers(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryMembersSignature {
  /** */
  (query: MemberQuery, options?: QueryMembersOptions): Promise<
    NonNullablePaths<
      QueryMembersResponse,
      | `members`
      | `members.${number}.status`
      | `members.${number}.profile.photo._id`
      | `members.${number}.profile.photo.url`
      | `members.${number}.profile.photo.height`
      | `members.${number}.profile.photo.width`
      | `members.${number}.privacyStatus`
      | `members.${number}.activityStatus`,
      6
    >
  >;
}

export function muteMember(httpClient: HttpClient): MuteMemberSignature {
  return (_id: string) =>
    universalMuteMember(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface MuteMemberSignature {
  /**
   * Mutes a member.
   *
   * Muted members can't engage with a community. For example, they can't leave comments, like posts or comments, or share content.
   *
   * You can mute members in [Wix Blog](https://dev.wix.com/docs/rest/business-solutions/blog/introduction) and [Wix Forum](https://dev.wix.com/docs/rest/business-solutions/forum/introduction).
   *
   * To unmute a member, call [Unmute Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/unmute-member).
   * @param - ID of the member to mute.
   */
  (_id: string): Promise<
    NonNullablePaths<
      MuteMemberResponse,
      | `member.status`
      | `member.contact.phones`
      | `member.contact.emails`
      | `member.contact.addresses`
      | `member.contact.addresses.${number}.streetAddress.number`
      | `member.contact.addresses.${number}.streetAddress.name`
      | `member.profile.photo._id`
      | `member.profile.photo.url`
      | `member.profile.photo.height`
      | `member.profile.photo.width`
      | `member.privacyStatus`
      | `member.activityStatus`,
      7
    >
  >;
}

export function unmuteMember(httpClient: HttpClient): UnmuteMemberSignature {
  return (_id: string) =>
    universalUnmuteMember(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface UnmuteMemberSignature {
  /**
   * Unmutes a member.
   *
   * Unmuted members can engage with the community. For example, leave comments, like posts or comments, and share content.
   *
   * You can unmute members in [Wix Blog](https://dev.wix.com/docs/rest/business-solutions/blog/introduction) and [Wix Forum](https://dev.wix.com/docs/rest/business-solutions/forum/introduction).
   *
   * To mute a member, call [Mute Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/mute-member).
   * @param - ID of the member to unmute.
   */
  (_id: string): Promise<
    NonNullablePaths<
      UnmuteMemberResponse,
      | `member.status`
      | `member.contact.phones`
      | `member.contact.emails`
      | `member.contact.addresses`
      | `member.contact.addresses.${number}.streetAddress.number`
      | `member.contact.addresses.${number}.streetAddress.name`
      | `member.profile.photo._id`
      | `member.profile.photo.url`
      | `member.profile.photo.height`
      | `member.profile.photo.width`
      | `member.privacyStatus`
      | `member.activityStatus`,
      7
    >
  >;
}

export function approveMember(httpClient: HttpClient): ApproveMemberSignature {
  return (_id: string) =>
    universalApproveMember(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface ApproveMemberSignature {
  /**
   * Changes member status to `"APPROVED"` and gives access to members-only pages.
   *
   * Call this API to:
   * - Approve a pending member.
   * - Unblock a blocked member.
   *
   * To approve multiple members, call [Bulk Approve Members](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/bulk-approve-members).
   *
   * To block a member, call [Block Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/block-member).
   * @param - ID of the member to approve.
   */
  (_id: string): Promise<
    NonNullablePaths<
      ApproveMemberResponse,
      | `member.status`
      | `member.contact.phones`
      | `member.contact.emails`
      | `member.contact.addresses`
      | `member.contact.addresses.${number}.streetAddress.number`
      | `member.contact.addresses.${number}.streetAddress.name`
      | `member.profile.photo._id`
      | `member.profile.photo.url`
      | `member.profile.photo.height`
      | `member.profile.photo.width`
      | `member.privacyStatus`
      | `member.activityStatus`,
      7
    >
  >;
}

export function blockMember(httpClient: HttpClient): BlockMemberSignature {
  return (_id: string) =>
    universalBlockMember(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface BlockMemberSignature {
  /**
   * Blocks a member.
   *
   * A blocked member can't log in to members-only pages.
   *
   * To block multiple members, call [Bulk Block Members](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/bulk-block-members).
   *
   * To unblock a member, call [Approve Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/approve-member).
   * @param - ID of a member to block.
   */
  (_id: string): Promise<
    NonNullablePaths<
      BlockMemberResponse,
      | `member.status`
      | `member.contact.phones`
      | `member.contact.emails`
      | `member.contact.addresses`
      | `member.contact.addresses.${number}.streetAddress.number`
      | `member.contact.addresses.${number}.streetAddress.name`
      | `member.profile.photo._id`
      | `member.profile.photo.url`
      | `member.profile.photo.height`
      | `member.profile.photo.width`
      | `member.privacyStatus`
      | `member.activityStatus`,
      7
    > & {
      __applicationErrorsType?: BlockMemberApplicationErrors;
    }
  >;
}

export function disconnectMember(
  httpClient: HttpClient
): DisconnectMemberSignature {
  return (_id: string) =>
    universalDisconnectMember(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface DisconnectMemberSignature {
  /**
   * Disconnects a member.
   *
   * A disconnected member can't log in to members-only pages, isn't visible in the dashboard,
   * and their `status` changes to `OFFLINE`.
   *
   * Use this when you want to permanently remove access without deleting member data.
   *
   * >**Note:** This action is irreversible. To connect the same member again, you have to create a member with a new slug.
   * @param - ID of a member to disconnect.
   */
  (_id: string): Promise<
    NonNullablePaths<
      DisconnectMemberResponse,
      | `member.status`
      | `member.contact.phones`
      | `member.contact.emails`
      | `member.contact.addresses`
      | `member.contact.addresses.${number}.streetAddress.number`
      | `member.contact.addresses.${number}.streetAddress.name`
      | `member.profile.photo._id`
      | `member.profile.photo.url`
      | `member.profile.photo.height`
      | `member.profile.photo.width`
      | `member.privacyStatus`
      | `member.activityStatus`,
      7
    >
  >;
}

export function deleteMember(httpClient: HttpClient): DeleteMemberSignature {
  return (_id: string) =>
    universalDeleteMember(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteMemberSignature {
  /**
   * Deletes a member by ID.
   *
   * All content created by this member is transferred to a site owner's account. For example, if a Wix user had blog posts, those posts are transferred to the site owner's account, which then becomes the owner of those posts.
   *
   * To delete the current member, call [Delete My Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/delete-my-member).
   *
   * To delete multiple members by `memberId`, call [Bulk Delete Members](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/bulk-delete-members).
   *
   * To delete multiple members by a specified filter, call [Bulk Delete Members By Filter](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/bulk-delete-members-by-filter).
   * @param - ID of a member to delete.
   */
  (_id: string): Promise<
    void & {
      __validationErrorsType?: DeleteMemberValidationErrors;
    }
  >;
}

export function deleteMyMember(
  httpClient: HttpClient
): DeleteMyMemberSignature {
  return (options?: DeleteMyMemberOptions) =>
    universalDeleteMyMember(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteMyMemberSignature {
  /**
   * Deletes the current member.
   *
   * After calling this method, the member is logged out of the site.
   *
   * All content created by this member is transferred to another account. For example, if a Wix user had blog posts, those posts are transferred to the account specified in `contentAssignedId`, which then becomes the owner of those posts.
   *
   * >**Note:**
   * >This method requires [visitor or member authentication](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).
   *
   * To delete a member by `ID`, call [Delete Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/delete-member).
   *
   * To delete multiple members by `memberId`, call [Bulk Delete Members](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/bulk-delete-members).
   *
   * To delete multiple members by a specified filter, call [Bulk Delete Members By Filter](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/bulk-delete-members-by-filter).
   */
  (options?: DeleteMyMemberOptions): Promise<
    void & {
      __validationErrorsType?: DeleteMyMemberValidationErrors;
    }
  >;
}

export function bulkDeleteMembers(
  httpClient: HttpClient
): BulkDeleteMembersSignature {
  return (memberIds: string[]) =>
    universalBulkDeleteMembers(
      memberIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteMembersSignature {
  /**
   * Deletes multiple members by `memberId`.
   *
   * All content created by these members is transferred to a site owner's account. For example, if Wix users had blog posts, those posts are transferred to the site owner's account, which then becomes the owner of those posts.
   *
   * To delete only the current member, call [Delete My Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/delete-my-member).
   *
   * To delete a single member by `ID`, call [Delete Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/delete-member).
   *
   * To delete multiple members by a specified filter, call [Bulk Delete Members By Filter](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/bulk-delete-members-by-filter).
   * @param - IDs of members to be deleted.
   */
  (memberIds: string[]): Promise<
    NonNullablePaths<
      BulkDeleteMembersResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkDeleteMembersByFilter(
  httpClient: HttpClient
): BulkDeleteMembersByFilterSignature {
  return (filter: any, options?: BulkDeleteMembersByFilterOptions) =>
    universalBulkDeleteMembersByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteMembersByFilterSignature {
  /**
   * Deletes multiple members by the specified filter.
   *
   * All content created by these members is transferred to a site owner's account. For example, if Wix users had blog posts, those posts are transferred to the site owner's account, which then becomes the owner of those posts.
   *
   * To delete only the current member, call [Delete My Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/delete-my-member).
   *
   * To delete a single member by `ID`, call [Delete Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/delete-member).
   *
   * To delete multiple members by `memberId`, call [Bulk Delete Members](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/bulk-delete-members).
   * @param - Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language) for more details.
   */
  (filter: any, options?: BulkDeleteMembersByFilterOptions): Promise<
    NonNullablePaths<BulkDeleteMembersByFilterResponse, `jobId`, 2>
  >;
}

export function bulkApproveMembers(
  httpClient: HttpClient
): BulkApproveMembersSignature {
  return (filter: any) =>
    universalBulkApproveMembers(
      filter,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkApproveMembersSignature {
  /**
   * Changes status of multiple members to `"APPROVED"`, and gives access to members-only pages.
   *
   * Call this API to:
   * - Approve pending members.
   * - Unblock blocked members.
   *
   * To approve a single member, call [Approve Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/approve-member).
   * @param - Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language) for more details.
   */
  (filter: any): Promise<
    NonNullablePaths<BulkApproveMembersResponse, `jobId`, 2>
  >;
}

export function bulkBlockMembers(
  httpClient: HttpClient
): BulkBlockMembersSignature {
  return (filter: any) =>
    universalBulkBlockMembers(
      filter,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkBlockMembersSignature {
  /**
   * Blocks multiple members by a specified filter.
   *
   * Blocked members can't log in to members-only pages.
   *
   * To block a single member, call [Block Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/block-member).
   *
   * To unblock a single member, call [Approve Member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/approve-member).
   *
   * To unblock multiple members, call [Bulk Approve Members](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/member-management/members/bulk-approve-members).
   * @param - Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language) for more details.
   */
  (filter: any): Promise<
    NonNullablePaths<BulkBlockMembersResponse, `jobId`, 2>
  >;
}

export function createMember(httpClient: HttpClient): CreateMemberSignature {
  return (options?: CreateMemberOptions) =>
    universalCreateMember(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateMemberSignature {
  /**
   * Creates a site member.
   *
   * After creation, you can call
   * [Send Set Password Email](https://dev.wix.com/docs/rest/crm/members-contacts/members/member-authentication/send-set-password-email)
   * to email the member with a link to set their password.
   * The member can log in to the site
   * when they set their password for the first time.
   *
   * > **Note:**
   * > When creating multiple members,
   * > set your requests at least 1 second apart to keep below rate limits.
   * @returns New member.
   */
  (options?: CreateMemberOptions): Promise<
    NonNullablePaths<
      Member,
      | `status`
      | `contact.phones`
      | `contact.emails`
      | `contact.addresses`
      | `contact.addresses.${number}.streetAddress.number`
      | `contact.addresses.${number}.streetAddress.name`
      | `profile.photo._id`
      | `profile.photo.url`
      | `profile.photo.height`
      | `profile.photo.width`
      | `privacyStatus`
      | `activityStatus`,
      6
    >
  >;
}

export function updateMember(httpClient: HttpClient): UpdateMemberSignature {
  return (_id: string, member: UpdateMember) =>
    universalUpdateMember(
      _id,
      member,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateMemberSignature {
  /**
   * Updates a member's properties.
   *
   * Only the requested fields are updated.
   * To clear a field's value, set an empty value with an empty string `""`.
   *
   * > **Note:**
   * > Updating the `contact.addresses`, `contact.emails`, or `contact.phones` array overwrites the entire array, so any existing values you want to retain must be passed in the `updateMember()` call along with the new values to add.
   * > However, passing an empty array will have no effect, and these methods must be used to clear all data from the respective array:
   * >- To clear `contact.addresses`, use `deleteMemberAddresses()`.
   * >- To clear `contact.emails`, use `deleteMemberEmails()`.
   * >- To clear `contact.phones`, use `deleteMemberPhones()`.
   *
   * > **Note:**
   * > Only logged-in members can call this method without elevated permissions.
   * > To call this method as a different identity, [elevated permissions](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/about-elevated-permissions) are required.
   * @param - Member ID.
   * @param - Member to update.
   * @returns Updated member.
   */
  (_id: string, member: UpdateMember): Promise<
    NonNullablePaths<
      Member,
      | `status`
      | `contact.phones`
      | `contact.emails`
      | `contact.addresses`
      | `contact.addresses.${number}.streetAddress.number`
      | `contact.addresses.${number}.streetAddress.name`
      | `profile.photo._id`
      | `profile.photo.url`
      | `profile.photo.height`
      | `profile.photo.width`
      | `privacyStatus`
      | `activityStatus`,
      6
    > & {
      __validationErrorsType?: UpdateMemberValidationErrors;
    }
  >;
}

export function deleteMemberPhones(
  httpClient: HttpClient
): DeleteMemberPhonesSignature {
  return (_id: string) =>
    universalDeleteMemberPhones(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteMemberPhonesSignature {
  /**
   * Clears a member's phone numbers.
   *
   * `deleteMemberPhones()` clears the `phones` array under the `contact` property.
   *
   * > **Note:**
   * > Only logged-in members can call this method without elevated permissions.
   * > To call this method as a different identity, [elevated permissions](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/about-elevated-permissions) are required.
   * @param - ID of the member whose phone numbers will be deleted.
   */
  (_id: string): Promise<
    NonNullablePaths<
      DeleteMemberPhonesResponse,
      | `member.status`
      | `member.contact.phones`
      | `member.contact.emails`
      | `member.contact.addresses`
      | `member.contact.addresses.${number}.streetAddress.number`
      | `member.contact.addresses.${number}.streetAddress.name`
      | `member.profile.photo._id`
      | `member.profile.photo.url`
      | `member.profile.photo.height`
      | `member.profile.photo.width`
      | `member.privacyStatus`
      | `member.activityStatus`,
      7
    >
  >;
}

export function deleteMemberEmails(
  httpClient: HttpClient
): DeleteMemberEmailsSignature {
  return (_id: string) =>
    universalDeleteMemberEmails(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteMemberEmailsSignature {
  /**
   * Clears a member's email addresses.
   *
   * `deleteMemberEmails()` clears the `emails` array under the `contact` property.
   *
   * > **Notes:**
   * > A member can still log in with their `loginEmail`,
   * > which is not cleared when this method is called.
   *
   * > Only logged-in members can call this method without elevated permissions.
   * > To call this method as a different identity, [elevated permissions](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/about-elevated-permissions) are required.
   * @param - ID of the member whose email addresses will be deleted.
   */
  (_id: string): Promise<
    NonNullablePaths<
      DeleteMemberEmailsResponse,
      | `member.status`
      | `member.contact.phones`
      | `member.contact.emails`
      | `member.contact.addresses`
      | `member.contact.addresses.${number}.streetAddress.number`
      | `member.contact.addresses.${number}.streetAddress.name`
      | `member.profile.photo._id`
      | `member.profile.photo.url`
      | `member.profile.photo.height`
      | `member.profile.photo.width`
      | `member.privacyStatus`
      | `member.activityStatus`,
      7
    >
  >;
}

export function deleteMemberAddresses(
  httpClient: HttpClient
): DeleteMemberAddressesSignature {
  return (_id: string) =>
    universalDeleteMemberAddresses(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteMemberAddressesSignature {
  /**
   * Deletes a member's street addresses.
   *
   * `deleteMemberAddresses()` clears the `addresses` array under the `contact` property.
   *
   * > **Note:**
   * > Only logged-in members can call this method without elevated permissions.
   * > To call this method as a different identity, [elevated permissions](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/about-elevated-permissions) are required.
   * @param - ID of the member whose street addresses will be deleted.
   */
  (_id: string): Promise<
    NonNullablePaths<
      DeleteMemberAddressesResponse,
      | `member.status`
      | `member.contact.phones`
      | `member.contact.emails`
      | `member.contact.addresses`
      | `member.contact.addresses.${number}.streetAddress.number`
      | `member.contact.addresses.${number}.streetAddress.name`
      | `member.profile.photo._id`
      | `member.profile.photo.url`
      | `member.profile.photo.height`
      | `member.profile.photo.width`
      | `member.privacyStatus`
      | `member.activityStatus`,
      7
    >
  >;
}

export const onMemberCreated = EventDefinition(
  'wix.members.v1.member_created',
  true,
  (event: MemberCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.lastLoginDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<MemberCreatedEnvelope>();
export const onMemberDeleted = EventDefinition(
  'wix.members.v1.member_deleted',
  true,
  (event: MemberDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'undefined.lastLoginDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<MemberDeletedEnvelope>();
export const onMemberUpdated = EventDefinition(
  'wix.members.v1.member_updated',
  true,
  (event: MemberUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.lastLoginDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<MemberUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ActiveSubscriptionMemberBlockForbiddenPayload,
  ActiveSubscriptionMemberDeleteForbiddenPayload,
  ActivityStatusStatus,
  Address,
  AddressStreetOneOf,
  App,
  ApplicationError,
  ApproveMemberRequest,
  ApproveMemberResponse,
  Asset,
  BaseEventMetadata,
  BlockMemberRequest,
  BlockMemberResponse,
  BulkActionMetadata,
  BulkApproveMembersRequest,
  BulkApproveMembersResponse,
  BulkBlockMembersRequest,
  BulkBlockMembersResponse,
  BulkDeleteMembersByFilterOptions,
  BulkDeleteMembersByFilterRequest,
  BulkDeleteMembersByFilterResponse,
  BulkDeleteMembersRequest,
  BulkDeleteMembersResponse,
  BulkMemberResult,
  Contact,
  ContentDeletionRequested,
  ContentReassignmentRequested,
  CreateMemberOptions,
  CreateMemberRequest,
  CreateMemberResponse,
  CursorPaging,
  CursorPagingMetadata,
  Cursors,
  CustomField,
  CustomTag,
  DeleteContext,
  DeleteMemberAddressesRequest,
  DeleteMemberAddressesResponse,
  DeleteMemberEmailsRequest,
  DeleteMemberEmailsResponse,
  DeleteMemberPhonesRequest,
  DeleteMemberPhonesResponse,
  DeleteMemberRequest,
  DeleteMemberResponse,
  DeleteMyMemberOptions,
  DeleteMyMemberRequest,
  DeleteMyMemberResponse,
  DeleteStatus,
  DisconnectMemberRequest,
  DisconnectMemberResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  File,
  GetCurrentMemberOptions,
  GetMemberOptions,
  GetMemberRequest,
  GetMemberResponse,
  GetMyMemberRequest,
  GetMyMemberResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  InvalidCustomFieldUrlPayload,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  ItemMetadata,
  JoinCommunityRequest,
  JoinCommunityResponse,
  LeaveCommunityRequest,
  LeaveCommunityResponse,
  ListMembersOptions,
  ListMembersRequest,
  ListMembersResponse,
  Member,
  MemberApproved,
  MemberBlocked,
  MemberCreatedEnvelope,
  MemberDeletedEnvelope,
  MemberIdChanged,
  MemberJoinedCommunity,
  MemberLeftCommunity,
  MemberMuted,
  MemberOwnershipTransferred,
  MemberQuerySpec,
  MemberSelfBlockForbiddenPayload,
  MemberToMemberBlockedPayload,
  MemberUnmuted,
  MemberUpdatedEnvelope,
  MembersQueryBuilder,
  MembersQueryResult,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  MuteMemberRequest,
  MuteMemberResponse,
  Namespace,
  NamespaceChanged,
  OdeditorAssigned,
  OdeditorUnassigned,
  OwnerMemberBlockForbiddenPayload,
  OwnerOrContributorDeleteForbiddenPayload,
  Page,
  Pages,
  Paging,
  PagingMetadata,
  PicassoAssigned,
  PicassoUnassigned,
  PrivacyStatusStatus,
  Profile,
  Query,
  QueryMembersOptions,
  QueryMembersRequest,
  QueryMembersResponse,
  RestoreInfo,
  Search,
  ServiceProvisioned,
  ServiceRemoved,
  Set,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  SlugAlreadyExistsPayload,
  SortOrder,
  Sorting,
  State,
  Status,
  StreetAddress,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  URI,
  URIs,
  UnmuteMemberRequest,
  UnmuteMemberResponse,
  UpdateMember,
  UpdateMemberRequest,
  UpdateMemberResponse,
  UpdateMemberSlugRequest,
  UpdateMemberSlugResponse,
  UpdateMySlugRequest,
  UpdateMySlugResponse,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  WebhookIdentityType,
  WixelAssigned,
  WixelUnassigned,
  utils,
} from './members-v1-member-members.universal.js';
